import { CalendarDays, Heading2, Heading3, ImagePlus, Info, List, ListOrdered, Minus, MousePointerClick, Pilcrow, Quote, Youtube } from "lucide-react";

const li = () => ({ type: "listItem", content: [{ type: "paragraph" }] });

/**
 * Every block the editor knows about – one registry for the slash menu, the palette and drag-and-drop. `node()` is the
 * JSON to insert; `convert` (optional) turns the current block into this type when chosen from the slash menu.
 */
export const BLOCKS = [
  { id: "paragraph", label: "Tekst", hint: "Vanleg brødtekst", icon: Pilcrow, keywords: "avsnitt p brødtekst", node: () => ({ type: "paragraph" }), convert: (c) => c.setParagraph() },
  { id: "h2", label: "Mellomtittel", hint: "Stor overskrift som deler saka opp", icon: Heading2, keywords: "overskrift heading h2 tittel", node: () => ({ type: "heading", attrs: { level: 2 } }), convert: (c) => c.setNode("heading", { level: 2 }) },
  { id: "h3", label: "Undertittel", hint: "Mindre overskrift", icon: Heading3, keywords: "overskrift heading h3", node: () => ({ type: "heading", attrs: { level: 3 } }), convert: (c) => c.setNode("heading", { level: 3 }) },
  { id: "ul", label: "Punktliste", hint: "Kulepunkt", icon: List, keywords: "liste punkt bullet", node: () => ({ type: "bulletList", content: [li()] }), convert: (c) => c.toggleBulletList() },
  { id: "ol", label: "Nummerert liste", hint: "1, 2, 3 …", icon: ListOrdered, keywords: "liste nummer tal", node: () => ({ type: "orderedList", content: [li()] }), convert: (c) => c.toggleOrderedList() },
  { id: "quote", label: "Sitat", hint: "Eit utdrag som skil seg ut", icon: Quote, keywords: "sitat quote", node: () => ({ type: "blockquote", content: [{ type: "paragraph" }] }), convert: (c) => c.toggleBlockquote() },
  { id: "image", label: "Bilete", hint: "Last opp eit foto med bilettekst", icon: ImagePlus, keywords: "bilete foto image figur", upload: true },
  { id: "callout", label: "Faktaboks", hint: "Praktisk info – tid, stad, pris", icon: Info, keywords: "faktaboks info godt å vite boks", node: () => ({ type: "callout", attrs: { tone: "sol", title: "Godt å vite" }, content: [{ type: "paragraph" }] }) },
  { id: "cta", label: "Knapp", hint: "Lenkje som ein stor knapp", icon: MousePointerClick, keywords: "knapp lenkje button cta billettar", node: () => ({ type: "cta", attrs: { label: "Kjøp billettar", href: "/arrangement", tone: "ink" } }) },
  { id: "event", label: "Arrangement", hint: "Kort for eit arrangement på huset", icon: CalendarDays, keywords: "arrangement event konsert kort", node: () => ({ type: "eventEmbed", attrs: { id: null, title: "" } }) },
  { id: "video", label: "Video", hint: "YouTube eller Vimeo", icon: Youtube, keywords: "video youtube vimeo film", node: () => ({ type: "video", attrs: { url: "" } }) },
  { id: "hr", label: "Skiljelinje", hint: "Ein tynn strek mellom to delar", icon: Minus, keywords: "strek linje hr skilje", node: () => ({ type: "horizontalRule" }) },
];

export const blockById = (id) => BLOCKS.find((b) => b.id === id);

export const filterBlocks = (query = "") => {
  const q = query.trim().toLowerCase();
  return q ? BLOCKS.filter((b) => `${b.label} ${b.hint} ${b.keywords}`.toLowerCase().includes(q)) : BLOCKS;
};

/** Position just after the top-level block under (x, y) – or before it when the pointer is in the block's upper half. */
export const blockBoundaryAt = (view, x, y) => {
  const hit = view.posAtCoords({ left: x, top: y });
  if (!hit) return view.state.doc.content.size;
  const $pos = view.state.doc.resolve(hit.inside >= 0 ? hit.inside : hit.pos);
  if ($pos.depth === 0) return hit.pos;
  const before = $pos.before(1), after = $pos.after(1);
  const dom = view.nodeDOM(before);
  if (dom?.getBoundingClientRect) { const r = dom.getBoundingClientRect(); return y < r.top + r.height / 2 ? before : after; }
  return after;
};

/** Insert block `id` at `pos` (or at the selection). Image blocks open the file picker registered by the editor. */
export const insertBlock = (editor, id, pos) => {
  const b = blockById(id);
  if (!b || !editor) return;
  if (b.upload) { editor.storage.hk?.pickImage?.(pos); return; }
  const at = pos ?? editor.state.selection.to;
  const chain = editor.chain().focus().insertContentAt(at, b.node());
  if (id === "paragraph" || id === "h2" || id === "h3") chain.setTextSelection(at + 1);
  chain.run();
};

/** Slash-menu choice: remove the typed «/query», then convert the block or insert the new one. */
export const runSlash = (editor, range, id) => {
  const b = blockById(id);
  if (!b) return;
  const c = editor.chain().focus().deleteRange(range);
  if (b.upload) { c.run(); editor.storage.hk?.pickImage?.(range.from); return; }
  if (b.convert) { b.convert(c).run(); return; }
  c.insertContent(b.node()).run();
};
